import { Form, Spin, type TableProps } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomButton,
  CustomDatePicker,
  CustomSelect,
  CustomTable,
  CustomTitleSection,
  CustomTimeRangePicker,
  CustomTypography,
} from "sipli-fleet-core/components";
import { useDebounce } from "src/hooks/useDebounce";
import { useQueryGetUnitBrandLookupByType } from "src/queries/administrationSetup.queries";
import {
  useQueryRawDataDeviceLookup,
  useQueryRawDataLogs,
} from "src/queries/rawDataLog.queries";
import type {
  RawDataDeviceLookupItem,
  RawDataLogParams,
  RawDataLogRecord,
} from "src/services/rawDataLogService";
import { useSelectDistributor } from "src/store/selectDistributorStore";
import "../Customers.css";
import "./RawDataLog.css";

interface RawDataLogProps {
  embedded?: boolean;
  initialImei?: string;
  initialSerial?: string;
  initialBrandName?: string;
  initialMakeId?: string | number;
}

interface RawDataLogFormValues {
  brandName?: string;
  deviceIdentifier?: string;
  logDate: Dayjs;
  timeRange?: [Dayjs | null, Dayjs | null] | null;
}

interface BrandOption {
  label: string;
  value: string;
  makeId?: string | number;
}

interface DeviceOption {
  label: string;
  value: string;
  imei?: string;
  serial?: string;
}

const DEFAULT_PAGINATION = { page: 0, size: 10 };
const QUECLINK_COLUMN_KEYS = new Set([
  "ID",
  "IMEI",
  "TIMESTAMP",
  "SYS_TIME",
  "OPERATION",
  "MSG_TYPE",
  "RAW",
  "DECODED",
]);
const TELTONIKA_COLUMN_KEYS = new Set([
  "IMEI",
  "CREATED_AT",
  "EVENT_ID",
  "GPS",
  "IO",
  "PRIORITY",
  "DATETIME",
]);

const normalizeColumnName = (value: unknown) =>
  String(value ?? "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();

const toDisplayText = (value: unknown): string => {
  if (value === undefined || value === null || value === "") return "--";
  if (typeof value === "string" || typeof value === "number") return String(value);

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const padDatePart = (value: unknown) => String(Number(value) || 0).padStart(2, "0");

const renderDateTime = (value: unknown) => {
  if (value === undefined || value === null || value === "") {
    return <span>{toDisplayText(value)}</span>;
  }

  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day, hour = 0, minute = 0] = value;

    return (
      <span className="flex flex-col whitespace-nowrap">
        <strong className="font-semibold">
          {padDatePart(day)}-{padDatePart(month)}-{toDisplayText(year)}
        </strong>
        <span>
          {padDatePart(hour)}:{padDatePart(minute)}
        </span>
      </span>
    );
  }

  const numericValue = Number(value);
  const normalizedValue =
    Number.isFinite(numericValue) && numericValue > 0 && numericValue < 1e12
      ? numericValue * 1000
      : value;
  const parsedDate = dayjs(normalizedValue as string | number | Date);

  if (!parsedDate.isValid()) {
    return <span>{toDisplayText(value)}</span>;
  }

  return (
    <span className="flex flex-col whitespace-nowrap">
      <strong className="font-semibold">{parsedDate.format("DD-MM-YYYY")}</strong>
      <span>{parsedDate.format("HH:mm")}</span>
    </span>
  );
};

const renderWrappedText = (value: unknown) => (
  <span className="block text-gray9 [overflow-wrap:anywhere]">
    {toDisplayText(value)}
  </span>
);

const renderLabelledLines = (value: unknown) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return (
      <span className="flex flex-col">
        {Object.entries(value as Record<string, unknown>).map(([key, item]) => (
          <span key={key}>
            <strong className="font-semibold">{key}:</strong> {toDisplayText(item)}
          </span>
        ))}
      </span>
    );
  }

  return renderWrappedText(value);
};

const renderIo = (value: unknown) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return Object.entries(value as Record<string, unknown>)
      .map(([key, item]) => `${key}: ${toDisplayText(item)}`)
      .join(", ");
  }

  return toDisplayText(value);
};

const getDeviceLabel = (item: RawDataDeviceLookupItem) => {
  const imei = item.imei ? `IMEI: ${item.imei}` : "";
  const serial = item.serial ? `S/N: ${item.serial}` : "";
  return [imei, serial].filter(Boolean).join(" · ") || item.name || "--";
};

const getOptionalDistributorId = (value: unknown) => {
  if (value === undefined || value === null || value === "") return undefined;
  return typeof value === "string" || typeof value === "number" ? value : undefined;
};

const RawDataLog = ({
  embedded = false,
  initialImei,
  initialSerial,
  initialBrandName,
  initialMakeId,
}: RawDataLogProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<RawDataLogFormValues>();
  const selectedBrandName = Form.useWatch("brandName", form);
  const { selectedDistributor } = useSelectDistributor();
  const initialDeviceIdentifier = initialImei || initialSerial;
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [sorting, setSorting] = useState<
    Pick<RawDataLogParams, "sort" | "sortBy">
  >({});
  const [submittedFilters, setSubmittedFilters] = useState<
    Pick<RawDataLogParams, "imei" | "brandName" | "date" | "fromTime" | "toTime"> | undefined
  >();
  const [deviceSearch, setDeviceSearch] = useState("");
  const [selectedMakeId, setSelectedMakeId] = useState<string | number | undefined>(
    initialMakeId,
  );
  const debouncedDeviceSearch = useDebounce(deviceSearch.trim(), 400);

  const { data: brandLookupData, isLoading: isBrandLookupLoading } =
    useQueryGetUnitBrandLookupByType(1, !embedded);
  const { data: deviceLookupData, isLoading: isDeviceLookupLoading } =
    useQueryRawDataDeviceLookup(
      debouncedDeviceSearch
        ? {
            query: debouncedDeviceSearch.toLowerCase(),
            distributorId: getOptionalDistributorId(selectedDistributor),
            makeId: selectedMakeId,
          }
        : undefined,
      { enabled: !embedded },
    );

  const queryParams = useMemo<RawDataLogParams | undefined>(() => {
    if (!submittedFilters) return undefined;

    return {
      ...submittedFilters,
      page: pagination.page,
      size: pagination.size,
      ...sorting,
    };
  }, [pagination.page, pagination.size, sorting, submittedFilters]);

  const { data: rawDataResponse, isLoading, isFetching } = useQueryRawDataLogs(
    queryParams,
    { enabled: !!submittedFilters },
  );

  const brandOptions = useMemo<BrandOption[]>(() => {
    const items = brandLookupData?.data?.response ?? [];
    const options = items.reduce<BrandOption[]>((result, item) => {
      if (!item.name) return result;
      return [...result, { label: item.name, value: item.name, makeId: item.id }];
    }, []);

    if (initialBrandName && !options.some((option) => option.value === initialBrandName)) {
      options.unshift({
        label: initialBrandName,
        value: initialBrandName,
        makeId: initialMakeId,
      });
    }

    return options;
  }, [brandLookupData, initialBrandName, initialMakeId]);

  const deviceOptions = useMemo<DeviceOption[]>(() => {
    const items = deviceLookupData?.data?.response ?? [];

    return items.reduce<DeviceOption[]>((result, item) => {
      const value = item.imei || item.serial;
      if (!value) return result;

      return [
        ...result,
        {
          label: getDeviceLabel(item),
          value,
          imei: item.imei,
          serial: item.serial,
        },
      ];
    }, []);
  }, [deviceLookupData]);

  useEffect(() => {
    if (!initialDeviceIdentifier) return;

    const date = dayjs();
    form.setFieldsValue({
      brandName: initialBrandName,
      deviceIdentifier: initialDeviceIdentifier,
      logDate: date,
    });
    setSelectedMakeId(initialMakeId);
    setPagination(DEFAULT_PAGINATION);
    setSubmittedFilters({
      imei: initialDeviceIdentifier,
      brandName: initialBrandName,
      date: date.format("YYYY-MM-DD"),
    });
  }, [form, initialBrandName, initialDeviceIdentifier, initialMakeId]);

  const response = rawDataResponse?.data?.response;
  const rows = response?.rawData ?? response?.content ?? response?.items ?? [];
  const totalCount = Number(
    response?.totalItems ??
      response?.totalElements ??
      response?.total ??
      response?.numberOfElements ??
      rows.length,
  );
  const availableColumns = useMemo(
    () =>
      response?.columns?.length
        ? new Set(response.columns.map(normalizeColumnName))
        : null,
    [response?.columns],
  );

  const allColumns = useMemo<TableProps<RawDataLogRecord>["columns"]>(
    () => [
      {
        title: t("id"),
        dataIndex: "id",
        key: "ID",
        width: "6%",
        render: toDisplayText,
      },
      {
        title: t("imei"),
        dataIndex: "imei",
        key: "IMEI",
        width: "12%",
        render: (value: unknown) => (
          <strong className="whitespace-nowrap">{toDisplayText(value)}</strong>
        ),
      },
      {
        title: t("timestamp"),
        dataIndex: "timestamp",
        key: "TIMESTAMP",
        width: "11%",
        render: renderDateTime,
      },
      {
        title: t("system_time"),
        dataIndex: "sys_time",
        key: "SYS_TIME",
        width: "11%",
        render: renderDateTime,
      },
      {
        title: t("operation"),
        dataIndex: "operation",
        key: "OPERATION",
        width: "8%",
        render: toDisplayText,
      },
      {
        title: t("message_type"),
        dataIndex: "msg_type",
        key: "MSG_TYPE",
        width: "9%",
        render: toDisplayText,
      },
      {
        title: t("raw_data"),
        dataIndex: "raw",
        key: "RAW",
        width: "22%",
        render: (_value: unknown, record) =>
          renderWrappedText(record.raw ?? record.raw_data_log),
      },
      {
        title: t("decoded"),
        dataIndex: "decoded",
        key: "DECODED",
        width: "21%",
        render: renderWrappedText,
      },
      {
        title: t("created_at"),
        dataIndex: "createdAt",
        key: "CREATED_AT",
        width: "13%",
        render: renderDateTime,
      },
      {
        title: t("event_id"),
        dataIndex: "eventId",
        key: "EVENT_ID",
        width: "9%",
        render: (value: unknown) => <strong>{toDisplayText(value)}</strong>,
      },
      {
        title: "GPS",
        dataIndex: "gps",
        key: "GPS",
        width: "16%",
        render: renderLabelledLines,
      },
      {
        title: "IO",
        dataIndex: "io",
        key: "IO",
        width: "31%",
        render: (value: unknown) => (
          <span className="block text-gray9 [overflow-wrap:anywhere]">
            {renderIo(value)}
          </span>
        ),
      },
      {
        title: t("priority"),
        dataIndex: "priority",
        key: "PRIORITY",
        width: "7%",
        render: toDisplayText,
      },
      {
        title: t("date_time"),
        dataIndex: "dateTime",
        key: "DATETIME",
        width: "11%",
        render: renderDateTime,
      },
    ],
    [t],
  );

  const columns = useMemo(() => {
    if (!availableColumns) {
      const defaultColumnKeys =
        normalizeColumnName(selectedBrandName) === "teltonika"
          ? TELTONIKA_COLUMN_KEYS
          : QUECLINK_COLUMN_KEYS;

      return allColumns?.filter((column) =>
        defaultColumnKeys.has(String(column?.key ?? "")),
      );
    }

    const conditionByKey: Record<string, string> = {
      ID: "id",
      IMEI: "imei",
      TIMESTAMP: "timestamp",
      SYS_TIME: "systime",
      OPERATION: "operation",
      MSG_TYPE: "msgtype",
      RAW: "raw",
      DECODED: "decoded",
      CREATED_AT: "createdat",
      EVENT_ID: "eventid",
      GPS: "gps",
      IO: "io",
      PRIORITY: "priority",
      DATETIME: "datetime",
    };

    const filteredColumns = allColumns?.filter((column) => {
      const key = String(column?.key ?? "");
      if (key === "RAW") {
        return availableColumns.has("raw") || availableColumns.has("rawdatalog");
      }
      return availableColumns.has(conditionByKey[key] ?? normalizeColumnName(key));
    });

    return filteredColumns?.length ? filteredColumns : allColumns;
  }, [allColumns, availableColumns, selectedBrandName]);

  const handleSubmit = (values: RawDataLogFormValues) => {
    const fromTime = values.timeRange?.[0]?.format("HH:mm:ss");
    const toTime = values.timeRange?.[1]?.format("HH:mm:ss");

    setPagination((current) => ({ ...current, page: 0 }));
    setSubmittedFilters({
      imei: values.deviceIdentifier || initialDeviceIdentifier || "",
      brandName: values.brandName || initialBrandName,
      date: values.logDate.format("YYYY-MM-DD"),
      ...(fromTime ? { fromTime } : {}),
      ...(toTime ? { toTime } : {}),
    });
  };

  const handleTableChange: TableProps<RawDataLogRecord>["onChange"] = (
    _tablePagination,
    _tableFilters,
    sorter,
  ) => {
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const sortBy = activeSorter?.columnKey
      ? String(activeSorter.columnKey)
      : undefined;
    const sort =
      activeSorter?.order === "ascend"
        ? "ASC"
        : activeSorter?.order === "descend"
          ? "DESC"
          : undefined;

    setSorting(sortBy && sort ? { sortBy, sort } : {});
    setPagination((current) => ({ ...current, page: 0 }));
  };

  const initialDeviceOption = initialDeviceIdentifier
    ? [{
        label: getDeviceLabel({ imei: initialImei, serial: initialSerial }),
        value: initialDeviceIdentifier,
        imei: initialImei,
        serial: initialSerial,
      }]
    : [];
  const resolvedDeviceOptions = [
    ...initialDeviceOption,
    ...deviceOptions.filter((option) => option.value !== initialDeviceIdentifier),
  ];

  const filters = (
    <Form<RawDataLogFormValues>
      form={form}
      layout="vertical"
      requiredMark={false}
      initialValues={{
        brandName: initialBrandName,
        deviceIdentifier: initialDeviceIdentifier,
        logDate: dayjs(),
        timeRange: null,
      }}
      onFinish={handleSubmit}
      className={embedded ? "!mb-0" : "mb-6 mt-6"}
    >
      <div
        className={`flex flex-wrap items-start gap-2 ${embedded ? "" : "min-h-[90px] py-4"}`}
      >
        {!embedded ? (
          <>
            <Form.Item
              name="brandName"
              label={t("device_brand")}
              className="raw-data-log-form-item !mb-0 !w-[216px] max-[900px]:!w-full"
              rules={[{ required: true, message: t("field_required") }]}
            >
              <CustomSelect
                className="raw-data-log-control"
                placeholder={t("select_device_brand")}
                options={brandOptions}
                loading={isBrandLookupLoading}
                showSearch
                optionFilterProp="label"
                onChange={(_value, option) => {
                  const selectedOption = option as BrandOption;
                  setSelectedMakeId(selectedOption.makeId);
                  form.setFieldValue("deviceIdentifier", undefined);
                  setDeviceSearch("");
                }}
              />
            </Form.Item>

            <Form.Item
              name="deviceIdentifier"
              label={t("device_identifier")}
              className="raw-data-log-form-item !mb-0 !w-[216px] max-[900px]:!w-full"
              rules={[{ required: true, message: t("field_required") }]}
            >
              <CustomSelect
                className="raw-data-log-control"
                placeholder={t("search_by_imei_or_serial")}
                showSearch
                allowClear
                filterOption={false}
                options={resolvedDeviceOptions}
                onSearch={setDeviceSearch}
                onClear={() => setDeviceSearch("")}
                notFoundContent={
                  isDeviceLookupLoading ? (
                    <Spin size="small" />
                  ) : debouncedDeviceSearch.length < 3 ? (
                    t("type_at_least_3_characters")
                  ) : (
                    t("no_data")
                  )
                }
              />
            </Form.Item>
          </>
        ) : null}

        <Form.Item
          name="logDate"
          label={t("log_date")}
          className="raw-data-log-form-item !mb-0 !w-[256px] max-[900px]:!w-full"
          rules={[{ required: true, message: t("field_required") }]}
        >
          <CustomDatePicker
            className="raw-data-log-control"
            placeholder={t("select_date")}
            isFutureDateAvailable={false}
            allowClear={false}
          />
        </Form.Item>

        <Form.Item
          name="timeRange"
          label={t("time_range")}
          className="raw-data-log-form-item !mb-0 !w-[228px] max-[900px]:!w-full"
        >
          <CustomTimeRangePicker
            className="raw-data-log-time-range !w-[228px] max-[900px]:!w-full"
            format="HH:mm"
            placeholder={[t("time_start"), t("time_end")]}
            allowClear
          />
        </Form.Item>

        <Form.Item className="!mb-0 !mt-[26px] !w-auto">
          <CustomButton
            htmlType="submit"
            type="primary"
            buttonSize="medium"
            label={t("search")}
            loading={isLoading || isFetching}
            className="!px-4"
          />
        </Form.Item>
      </div>
    </Form>
  );

  const table = (
    <CustomTable<RawDataLogRecord>
      customClass="customers-table raw-data-log-table"
      columns={columns}
      data={rows}
      isLoading={isLoading || isFetching}
      showSearchInput={false}
      showSorting={false}
      isShowPagination
      pageSize={pagination.size}
      currentPage={pagination.page}
      totalCount={totalCount}
      handlePagination={(page) =>
        setPagination((current) => ({ ...current, page }))
      }
      handlePageSizeChange={(size) =>
        setPagination({ page: 0, size: Number(size) })
      }
      onChange={handleTableChange}
      rowKey={(record) =>
        String(
          record.id ??
            `${record.imei ?? "device"}-${toDisplayText(record.timestamp)}-${toDisplayText(record.sys_time)}`,
        )
      }
      tableLayout="fixed"
      locale={{ emptyText: t("no_data") }}
    />
  );

  return (
    <section
      className={
        embedded
          ? "raw-data-log raw-data-log-embedded"
          : "raw-data-log min-h-full bg-gray1 pb-8 pl-2"
      }
    >
      {!embedded ? (
        <CustomTitleSection
          titleKey="raw_data_log"
          showBtn={false}
          classNameContainer="!h-16 !items-start !py-0 !pt-6"
          titleClassName="!text-[25.23px] !font-[700] !leading-[28px]"
        />
      ) : null}

      {embedded ? (
        <div className="flex flex-col gap-6 overflow-hidden rounded-[16px] bg-white">
          <div className="flex flex-col gap-6 p-4">
            <CustomTypography.CustomTitle
              level={4}
              className="!m-0 !text-[19.93px] !font-bold !leading-7"
            >
              {t("raw_data_log")}
            </CustomTypography.CustomTitle>
            {filters}
          </div>
          {table}
        </div>
      ) : (
        <>
          {filters}
          <div className="overflow-hidden rounded-[16px] bg-white">{table}</div>
        </>
      )}
    </section>
  );
};

export default RawDataLog;
